// Připomínky a notifikace (docs/SPEC.md 2.8): pravidla, kanály, tiché hodiny, zařízení.
import { z } from 'zod'
import type { reminderChannelSchema } from './enums'
import { reminderStatusSchema } from './enums'

/** Pravidla připomínek (čas organizace). Dedupe: jedna připomínka na (entita, pravidlo, uživatel, kanál). */
export const REMINDER_RULES = [
  'promise_d-1_18',
  'promise_d_7',
  'event_-60m',
  'task_d_7',
  'morning_digest',
  /** Připomínka navržená Buddym (schedule_reminder) s vlastním časem. */
  'custom',
] as const
export type ReminderRule = (typeof REMINDER_RULES)[number]

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Čas zadej jako HH:MM')

export const notificationPrefsSchema = z.object({
  channels: z.object({
    push: z.boolean(),
    email: z.boolean(),
    sms: z.boolean(),
  }),
  /** Tiché hodiny: push se odloží na konec, email jde hned. Výchozí 20:00 až 07:00. */
  quietFrom: hhmm,
  quietTo: hhmm,
})
export type NotificationPrefs = z.infer<typeof notificationPrefsSchema>

export const updateNotificationPrefsSchema = notificationPrefsSchema.partial({
  channels: true,
  quietFrom: true,
  quietTo: true,
})
export type UpdateNotificationPrefsInput = z.infer<typeof updateNotificationPrefsSchema>

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  channels: { push: true, email: true, sms: false },
  quietFrom: '20:00',
  quietTo: '07:00',
}

/**
 * Registrace zařízení pro push: web posílá endpoint + keys (Web Push), nativní aplikace (android, ios)
 * token z FCM. Nativní zařízení se podle tokenu deduplikuje (opakovaná registrace jen obnoví lastSeenAt).
 */
export const pushSubscriptionInputSchema = z
  .object({
    platform: z.enum(['web', 'ios', 'android']).default('web'),
    endpoint: z.string().url().max(2000).optional(),
    keys: z.object({ p256dh: z.string().min(1), auth: z.string().min(1) }).optional(),
    token: z.string().min(1).max(4000).optional(),
    userAgent: z.string().max(300).optional(),
  })
  .superRefine((input, ctx) => {
    if (input.platform === 'web') {
      if (!input.endpoint) {
        ctx.addIssue({
          code: 'custom',
          path: ['endpoint'],
          message: 'Prohlížeč neposlal údaje k odběru.',
        })
      }
      if (!input.keys) {
        ctx.addIssue({
          code: 'custom',
          path: ['keys'],
          message: 'Prohlížeč neposlal klíče k odběru.',
        })
      }
    } else if (!input.token) {
      ctx.addIssue({ code: 'custom', path: ['token'], message: 'Chybí token zařízení.' })
    }
  })
export type PushSubscriptionInput = z.infer<typeof pushSubscriptionInputSchema>

export interface PushDevice {
  id: string
  platform: 'web' | 'ios' | 'android'
  userAgent: string | null
  lastSeenAt: string
  createdAt: string
  /** Je to zařízení, ze kterého přišel požadavek (web podle endpointu, nativní aplikace podle tokenu). */
  current: boolean
}

/** GET /v1/push/vapid: veřejný klíč, null = push vypnutý (chybí VAPID klíče). */
export interface VapidResponse {
  publicKey: string | null
}

export const listRemindersQuerySchema = z.object({
  status: reminderStatusSchema.optional(),
  from: z.string().datetime({ offset: true }).optional(),
  to: z.string().datetime({ offset: true }).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

export interface ReminderItem {
  id: string
  entityType: string
  entityId: string
  rule: string
  channel: z.infer<typeof reminderChannelSchema>
  dueAt: string
  status: z.infer<typeof reminderStatusSchema>
  sentAt: string | null
  title: string
  createdAt: string
}

/** Obsah notifikace (push i email mají stejné jádro). */
export interface NotificationPayload {
  title: string
  body: string
  /** Relativní cesta v aplikaci, kam klepnutí vede (např. /sliby). */
  url: string
  tag?: string
}

/** Náhled ranního přehledu (POST /v1/me/notifications/digest-test). */
export interface DigestPreview {
  title: string
  body: string
  /** Řádky přehledu (bez pozdravu a prázdných řádků). */
  lines: string[]
}

/** Odpověď na testovací odeslání ranního přehledu: kam došel a co v něm bylo. */
export interface DigestTestResponse {
  /** Kanály, kterými se přehled opravdu odeslal (kanál bez zařízení nebo vypnutý v nich není). */
  sent: Array<'push' | 'email'>
  /** Dnes není co posílat (nic se neodeslalo, `preview` je null). */
  empty: boolean
  preview: DigestPreview | null
}
